import { useCallback, useMemo } from 'react';

import { useUncontrolledProp } from '@toolbox/hooks';
import { CheckboxGroupContextProvider } from './CheckboxGroupContext';

import type React from 'react';
import type { ChangeEvent } from 'react';

export type CheckboxGroupProps<T> = {
  /**
   * The name used to reference the value of the control.
   * If you don't provide this prop, it falls back to a randomly generated name.
   */
  name: string;
  onChange?: (value: T[]) => void;
  value?: T[];
  defaultValue?: T[];
  children?: React.ReactNode;
};

export function CheckboxGroup<T>(props: CheckboxGroupProps<T>): JSX.Element {
  const { children, name, value: valueProp } = props;
  const [value, setValue] = useUncontrolledProp<T[]>(
    valueProp,
    props.defaultValue || [],
    props.onChange,
  );

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      const isChecked = event.target.checked;
      const selectedValue = event.target.value as unknown as T;

      setValue((value) =>
        isChecked ? [...value, selectedValue] : value.filter((v) => v === selectedValue),
      );
    },
    [setValue],
  );

  const group = useMemo(() => ({ name, onChange, value }), [name, onChange, value]);

  return <CheckboxGroupContextProvider<T[]> value={group}>{children}</CheckboxGroupContextProvider>;
}
